'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { uuidv7 } from 'uuidv7';
import { DEBUG } from '../../../../../../../constants/env';
import { usePageContext } from '../../../../../hooks/PageContext/usePageContext';
import { useFeatureFlag } from '../../../../../hooks/FeatureFlagsProvider';
import SlideableDrawerBanner from '../../../../ui/SlideableDrawer/SlideableDrawerBanner';
import Panel from '../../../../ui/Panel/Panel';
import PanelHeader from '../../../../ui/PanelHeader/PanelHeader';
import styles from './Fetch.module.css';
import { FetchTeaser } from './components/FetchTeaser';
import { FetchPanelLabel } from './components/FetchPanelLabel';
import { fetchDesignAnalyticsArm, useFetchDesign } from './fetchDesign';
import { FetchModal } from './components/FetchModal';
import { FetchPetScreen } from './components/FetchPetScreen';
import { FetchLoading } from './components/FetchLoading';
import { FetchError } from './components/FetchError';
import { FetchPlanScreen } from './components/FetchPlanScreen';
import { FetchConfirmationInline } from './components/FetchConfirmationInline';
import { fetchQuote, type FetchQuoteFailure } from './fetchQuote';
import { fetchSelect } from './fetchSelect';
import { fetchCustomer } from './fetchCustomer';
import { useFetchAnalytics, type FetchFormCloseVia } from './useFetchAnalytics';
import {
  fetchOrderKey,
  markFetchDeclined,
  markFetchEngaged,
  markFetchPurchased,
} from './fetchHistory';
import { useViewability } from '../Disco/hooks/useViewability';
import { getFetchBuyUrl } from './constants';
import { resolvePageUrl } from '../../willSkipAnalytics';
import type {
  FetchPetFormValues,
  FetchQuoteResult,
  FetchSelectInput,
} from './types';

interface FetchProps {
  appsJwt: string;
  fixedPanelHeight?: string;
  decisionId?: string;
}

type Phase =
  | 'teaser'
  | 'petForm'
  | 'quoting'
  | 'error'
  | 'planSelect'
  | 'selecting'
  | 'handedOff';

type PlanSelection = Omit<FetchSelectInput, 'cartId' | 'prospectPetId'>;

function Fetch({ appsJwt, decisionId }: FetchProps) {
  const {
    state: {
      retailerMoniker,
      deviceType: ctxDeviceType,
      orderNumbers,
      trackingNumbers,
      email: contextEmail,
      destinationZip,
      requestUrl,
      versionName,
      localeLanguage,
      localeCountry,
      trackingStatus,
      sessionId: ctxSessionId,
    },
  } = usePageContext();

  const rmFetchDesign = fetchDesignAnalyticsArm(
    useFeatureFlag('rm-fetch-design'),
  );
  const design = useFetchDesign();
  const isMobile = (ctxDeviceType ?? 'desktop') === 'mobile';
  const orderKey = fetchOrderKey(orderNumbers, trackingNumbers);
  const device = isMobile ? ('mobile' as const) : ('desktop' as const);
  const locale =
    localeLanguage && localeCountry
      ? `${localeLanguage}_${localeCountry}`
      : 'en_US';

  const [sessionId] = useState(() => ctxSessionId ?? uuidv7());
  const [quote, setQuote] = useState<FetchQuoteResult | null>(null);
  const [quoteFailure, setQuoteFailure] =
    useState<FetchQuoteFailure>('unknown');
  const [petInfo, setPetInfo] = useState<FetchPetFormValues | null>(null);
  const [phase, setPhase] = useState<Phase>('teaser');
  const [dismissed, setDismissed] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [requestClose, setRequestClose] = useState(false);
  const [prefillEmail, setPrefillEmail] = useState(contextEmail ?? '');
  const [prefillZip, setPrefillZip] = useState(destinationZip ?? '');

  const analyticsParams = useMemo(
    () => ({
      jwt: appsJwt,
      sessionId,
      retailerMoniker,
      device,
      pageUrl: resolvePageUrl(requestUrl),
      versionName: versionName ?? '',
      locale,
      orderNumber: orderNumbers?.[0],
      trackingNumber: trackingNumbers?.[0],
      trackingStatus: trackingStatus ?? undefined,
      decisionId,
      rmFetchDesign,
    }),
    [
      appsJwt,
      sessionId,
      retailerMoniker,
      device,
      requestUrl,
      versionName,
      locale,
      orderNumbers,
      trackingNumbers,
      trackingStatus,
      decisionId,
      rmFetchDesign,
    ],
  );

  const {
    trackWidgetView,
    trackCtaClick,
    trackOfferRequested,
    trackOfferDisplayed,
    trackOfferDeclined,
    trackFormClosed,
    trackCheckoutHandoff,
    trackPurchaseError,
  } = useFetchAnalytics(analyticsParams);

  const teaserRef = useViewability(() => {}, trackWidgetView);

  const prefillFetchedRef = useRef(false);
  useEffect(() => {
    const orderNumber = orderNumbers?.[0];
    const trackingNumber = trackingNumbers?.[0];
    if (prefillFetchedRef.current || (!orderNumber && !trackingNumber)) return;
    prefillFetchedRef.current = true;
    void fetchCustomer({
      jwt: appsJwt,
      sessionId,
      retailerMoniker,
      orderNumber,
      trackingNumber,
    }).then((result) => {
      if (result?.email) setPrefillEmail(result.email);
      if (result?.zip) setPrefillZip(result.zip);
    });
  }, [orderNumbers, trackingNumbers, appsJwt, sessionId, retailerMoniker]);

  const buyUrl = getFetchBuyUrl({
    buyUrl: quote?.buyUrl,
    deviceType: isMobile ? 'mobile' : 'desktop',
  });

  // Stamped every time the form opens rather than once at the CTA: the plan
  // screen's "Edit" reopens it, and timing from the CTA would then count the
  // quote call and plan browsing as time on the form.
  const formOpenedAt = useRef<number | null>(null);

  const openPetForm = useCallback(() => {
    formOpenedAt.current = performance.now();
    setPhase('petForm');
  }, []);

  const handleGetCovered = useCallback(() => {
    trackCtaClick();
    markFetchEngaged(orderKey);
    openPetForm();
  }, [trackCtaClick, orderKey, openPetForm]);

  const handlePetSubmit = useCallback(
    (pet: FetchPetFormValues) => {
      setPetInfo(pet);
      setPhase('quoting');
      trackOfferRequested();
      void fetchQuote({
        jwt: appsJwt,
        sessionId,
        retailerMoniker,
        orderNumber: orderNumbers?.[0],
        trackingNumber: trackingNumbers?.[0],
        device,
        locale,
        trackingStatus: trackingStatus ?? undefined,
        pet,
      }).then((result) => {
        if (result.ok) {
          setQuote(result.quote);
          setPhase('planSelect');
          trackOfferDisplayed();
        } else {
          setQuoteFailure(result.reason);
          setPhase('error');
        }
      });
    },
    [
      appsJwt,
      sessionId,
      retailerMoniker,
      orderNumbers,
      trackingNumbers,
      device,
      locale,
      trackingStatus,
      trackOfferRequested,
      trackOfferDisplayed,
    ],
  );

  const handlePlanSelect = useCallback(
    (selection: PlanSelection, monthlyPrice?: number) => {
      if (!quote) return;
      // buyUrl is known at render, so check it before committing the selection.
      // Bailing after fetchSelect drops back to the plan screen with nothing
      // shown and the button live again to re-fire the same mutation, and skips
      // trackCheckoutHandoff -- which leaves a gateway that stopped sending
      // buyUrl looking identical in analytics to nobody clicking.
      if (!buyUrl) {
        trackPurchaseError('missing_buy_url');
        setQuoteFailure('unknown');
        setPhase('error');
        return;
      }
      setPhase('selecting');
      // Opened synchronously so the click still counts as a user gesture -- a
      // window.open after the await below would be swallowed by popup blockers.
      // opener is severed so the Fetch tab cannot reach back into this one.
      const checkoutWindow = window.open('', '_blank');
      if (checkoutWindow) checkoutWindow.opener = null;
      void fetchSelect({
        jwt: appsJwt,
        sessionId,
        retailerMoniker,
        selection: {
          cartId: quote.cartIdentifier,
          prospectPetId: quote.prospectPetId,
          ...selection,
        },
      }).then((ok) => {
        if (!ok) {
          checkoutWindow?.close();
          setPhase('planSelect');
          return;
        }
        trackCheckoutHandoff({ ...selection, monthlyPrice });
        markFetchPurchased(orderKey);
        setPhase('handedOff');
        if (DEBUG) console.log('[fetch] handoff url', buyUrl);
        // If the popup was blocked anyway, fall back to this tab rather than
        // stranding the customer on a page with no way forward.
        if (checkoutWindow) checkoutWindow.location.assign(buyUrl);
        else window.location.assign(buyUrl);
      });
    },
    [
      appsJwt,
      sessionId,
      retailerMoniker,
      quote,
      buyUrl,
      orderKey,
      trackCheckoutHandoff,
      trackPurchaseError,
    ],
  );

  // Only a close from the form itself counts: submitting is offer_requested,
  // and closing from the quote or plan screens is leaving something else.
  const reportFormClosed = (closedVia: FetchFormCloseVia) => {
    if (phase !== 'petForm' || formOpenedAt.current === null) return;
    trackFormClosed({
      msOnForm: Math.round(performance.now() - formOpenedAt.current),
      closedVia,
    });
    formOpenedAt.current = null;
  };

  const dismiss = () => setDismissed(true);

  // "No thanks" is an answer about pet insurance rather than about this parcel,
  // so it is remembered for every order this browser tracks, not just this one.
  const recordDecline = () => {
    trackOfferDeclined();
    markFetchDeclined();
    dismiss();
  };

  // Desktop's "No thanks" removes the offer, so it is the decline. Mobile's only
  // closes the drawer -- what is left is the collapsed panel, still offering
  // "Get covered" -- so the decline there is that panel's button, not this one.
  const declineOffer = () => {
    if (isMobile) {
      setRequestClose(true);
      return;
    }
    recordDecline();
  };

  if (dismissed) return null;

  const isPetPhase = phase === 'petForm';
  const isQuoting = phase === 'quoting';
  const isErrorPhase = phase === 'error';
  const isPlanPhase =
    (phase === 'planSelect' || phase === 'selecting') && !!quote;
  const isFullScreen = isPetPhase || isQuoting || isErrorPhase || isPlanPhase;

  const screen = isPetPhase ? (
    <FetchPetScreen
      isMobile={isMobile}
      onSubmit={handlePetSubmit}
      initialEmail={prefillEmail}
      initialZip={prefillZip}
      initialValues={petInfo}
    />
  ) : isQuoting ? (
    <FetchLoading
      isMobile={isMobile}
      message="Getting your price from Fetch…"
    />
  ) : isErrorPhase ? (
    <FetchError
      isMobile={isMobile}
      failure={quoteFailure}
      onBack={openPetForm}
    />
  ) : isPlanPhase && quote ? (
    <FetchPlanScreen
      isMobile={isMobile}
      quote={quote}
      petInfo={petInfo}
      loading={phase === 'selecting'}
      onSelect={handlePlanSelect}
      onEdit={openPetForm}
    />
  ) : null;

  const teaser = (
    <div ref={phase === 'teaser' ? teaserRef : undefined}>
      <FetchTeaser
        onGetCovered={handleGetCovered}
        onNoThanks={declineOffer}
        isMobile={isMobile}
      />
    </div>
  );

  if (isMobile) {
    if (collapsed) {
      return (
        <Panel
          header={
            // Hoi's inline design: 1.x has no bar (its label is the chip);
            // the hand-off confirmation keeps the header in every arm.
            design.layout === 't1' && phase !== 'handedOff' ? null : (
              <PanelHeader>
                <FetchPanelLabel />
              </PanelHeader>
            )
          }
          sOuter={{ marginBottom: '0.5rem' }}
          sInner={{ padding: 0 }}
        >
          {phase === 'handedOff' ? (
            <FetchConfirmationInline isMobile buyUrl={buyUrl} />
          ) : (
            <FetchTeaser
              inline
              onGetCovered={() => {
                setCollapsed(false);
                handleGetCovered();
              }}
              onNoThanks={recordDecline}
            />
          )}
        </Panel>
      );
    }
    return (
      <SlideableDrawerBanner
        title={design.drawerTitle}
        delaySeconds={phase === 'teaser' ? 1 : 0}
        hideBannerTitle={design.layout === 't1' && !isFullScreen}
        blurOverlay={false}
        shouldClose={requestClose}
        fullHeight={isFullScreen}
        onDismiss={() => {
          reportFormClosed('drawer');
          setCollapsed(true);
          setRequestClose(false);
        }}
      >
        {isFullScreen ? (
          screen
        ) : phase === 'handedOff' ? (
          <FetchConfirmationInline isMobile buyUrl={buyUrl} />
        ) : (
          <div className={styles.mobileWrapper}>{teaser}</div>
        )}
      </SlideableDrawerBanner>
    );
  }

  const closeModal = () => setPhase('teaser');

  return (
    <>
      {phase === 'handedOff' ? (
        <FetchConfirmationInline buyUrl={buyUrl} />
      ) : (
        teaser
      )}
      {phase !== 'teaser' && phase !== 'handedOff' && (
        <FetchModal
          onClose={() => {
            reportFormClosed('close_button');
            closeModal();
          }}
          onBackdropClick={() => {
            reportFormClosed('backdrop');
            closeModal();
          }}
        >
          {isFullScreen ? (
            screen
          ) : (
            <div className={styles.modalPad}>{teaser}</div>
          )}
        </FetchModal>
      )}
    </>
  );
}

export default Fetch;
